import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../hd-content-language.service';
import { HdChironPosition, HdChironSide } from '../../hd-chiron.models';
import { HdAstroPointClickEvent, HdAstroPointPosition } from '../../hd-astro-points.models';
import { HdPlanetInfoService } from '../../hd-planet-info.service';
import { HdPlanetGridDensity } from '../hd-planet-activation-block/hd-planet-activation-block.component';
import {
  HD_CHIRON_DISPLAY_CONFIG,
  HdAstroPointKey,
  getHdAstroPointDisplayConfig,
} from '../hd-planet-display-config';

interface AstroPointCellVm {
  readonly key: HdAstroPointKey | 'chiron';
  readonly glyph: string;
  readonly gate: number;
  readonly line: number;
  /** `kleur.toon`, leeg wanneer de berekening ze niet leverde. */
  readonly colorTone: string;
  readonly tooltip: string;
}

/**
 * Astro posities onder de planetengrid (spec 2026-10-06): Chiron, AC, DC, MC, IC en Black Moon
 * Lilith van één zijde, als rij in de stijl van de Figma-cheat-sheet (glyph, poort, `.lijn` en
 * `kleur.toon`). Géén activaties: een klik opent enkel de gate-info van die poort met het punt als
 * informatieve regel, zoals bij Chiron.
 */
@Component({
  selector: 'hd-astro-points',
  templateUrl: './hd-astro-points.component.html',
  styleUrl: './hd-astro-points.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.side-design]': "side() === 'design'",
    '[class.side-personality]': "side() === 'personality'",
    '[class.density-mini]': "density() === 'mini'",
  },
})
export class HdAstroPointsComponent {
  readonly side = input.required<HdChironSide>();
  /** Chiron van deze zijde; `null` wanneer verborgen (setting) of afwezig. */
  readonly chiron = input<HdChironPosition | null>(null);
  readonly points = input<readonly HdAstroPointPosition[]>([]);
  readonly density = input<HdPlanetGridDensity>('normal');
  readonly pointClicked = output<HdAstroPointClickEvent>();

  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly cells = computed<readonly AstroPointCellVm[]>(() => {
    const lang = this.lang();
    const cells: AstroPointCellVm[] = [];
    const chiron = this.chiron();
    if (chiron) {
      const name = this.planetInfo.planetInfoIn('chiron', lang)?.name || HD_CHIRON_DISPLAY_CONFIG.label;
      cells.push(toCell('chiron', HD_CHIRON_DISPLAY_CONFIG.astronomiconChar, name, chiron));
    }
    for (const point of this.points()) {
      const config = getHdAstroPointDisplayConfig(point.key);
      const name = this.planetInfo.planetInfoIn(point.key, lang)?.name || config.label;
      cells.push(toCell(point.key, config.astronomiconChar, name, point));
    }
    return cells;
  });

  protected onClick(key: HdAstroPointKey | 'chiron', event: MouseEvent): void {
    this.pointClicked.emit({ key, side: this.side(), origin: { x: event.clientX, y: event.clientY } });
  }
}

function toCell(
  key: HdAstroPointKey | 'chiron',
  glyph: string,
  name: string,
  position: Pick<HdChironPosition, 'gate' | 'line' | 'color' | 'tone' | 'base'>,
): AstroPointCellVm {
  const hasColorTone = position.color > 0 && position.tone > 0;
  const chain = [position.gate, position.line, position.color, position.tone, position.base]
    .filter((part, index) => index < 2 || part > 0)
    .join('.');
  return {
    key,
    glyph,
    gate: position.gate,
    line: position.line,
    colorTone: hasColorTone ? `${position.color}.${position.tone}` : '',
    tooltip: `${name} ${chain}`,
  };
}
